/**
 * When an in-call event is worth telling someone about.
 *
 * Each rule here replaced a notice that fired on the EVENT rather than on a
 * CHANGE: a host's "mute all" told people who were already muted that the host
 * had muted them, a second recorder starting re-announced the first, and a
 * share that was already running when you joined was announced as starting.
 * The rule throughout: a notice describes something that just changed for the
 * person reading it, once.
 */

/** What a host's force command found running on this client when it landed. */
export type ForcedMediaState = {
  micOn: boolean
  camOn: boolean
  /** Additional cameras this client publishes besides the primary one. */
  extraCameras: number
  /** This client is sharing its screen. */
  sharing: boolean
}

export type ForcedAction = 'mute' | 'stop_video' | 'stop_screenshare'

/**
 * Whether a host's force command changed anything here, and so deserves the
 * "the host muted you" / "stopped your camera" / "stopped your share" notice.
 *
 * Bulk commands ("Mute all", "Stop all videos", "Stop all screens", and
 * switching a host control off, which sends them) reach EVERY participant,
 * because the server cannot tell who is affected. Someone whose mic was
 * already muted lost nothing; telling them the host muted them is false.
 * The command itself still runs for everyone — only the notice is decided here.
 */
export function forcedActionChanged(action: ForcedAction, state: ForcedMediaState): boolean {
  switch (action) {
    case 'mute':
      return state.micOn
    case 'stop_video':
      return state.camOn || state.extraCameras > 0
    case 'stop_screenshare':
      return state.sharing
  }
}

/**
 * Recorders who were not recording before — the ones a "started a local
 * recording" notice is about.
 *
 * The notice compared the whole list as a string: when Asha was joined by Ravi
 * it announced "Asha, Ravi started a local recording", and when Asha stopped
 * it announced "Ravi started a local recording" — about a recording that had
 * been running all along.
 */
export function newRecorders(before: readonly string[], after: readonly string[]): string[] {
  const known = new Set(before)
  return after.filter((name) => !known.has(name))
}

/**
 * What this client knows of each peer's screen share, and how it knows it.
 *
 *  - `announced`: the presenter said it started.
 *  - `seen`: this client receives the share, but has not heard it start —
 *    it was running before this client joined, or its stream simply got here
 *    first.
 *  - `ended`: the presenter said it stopped, and this client still holds the
 *    stream (it is being torn down).
 *
 * `seen` and `announced` were one state. The share's stream reaches a viewer
 * over the SFU socket, and the presenter only sends `started` once its
 * producers exist, over signalling — so the stream usually lands first. It was
 * then filed as "already running", and the `started` that followed a moment
 * later was dropped as a duplicate: someone began presenting and nobody was
 * told. After a stop the still-closing stream was filed the same way, which
 * swallowed the next share's start too.
 */
export type PresentingSet = ReadonlyMap<string, 'announced' | 'seen' | 'ended'>

/**
 * A peer announced a share. Announced once per share: a duplicate `started`
 * for a share already announced is not news. A share only *seen* so far is —
 * hearing it start means it has just started.
 */
export function presentingStarted(set: PresentingSet, peerId: string): { set: PresentingSet; announce: boolean } {
  if (set.get(peerId) === 'announced') return { set, announce: false }
  return { set: new Map(set).set(peerId, 'announced'), announce: true }
}

/**
 * A peer announced their share ended. Only a share this client knew about is
 * announced as ending, and only once.
 */
export function presentingStopped(set: PresentingSet, peerId: string): { set: PresentingSet; announce: boolean } {
  const known = set.get(peerId)
  if (known === undefined || known === 'ended') return { set, announce: false }
  return { set: new Map(set).set(peerId, 'ended'), announce: true }
}

/**
 * Keep the set in step with the room, silently.
 *
 * `sharingNow` is who this client currently receives a screen from. A share
 * received that nothing was heard about is recorded as `seen` without a word:
 * its end is news, and so is its start if that is still to come. Nobody is
 * dropped for their stream going away (this client's own media rebuilding
 * drops and re-receives every stream; the share never stopped) — except a
 * share already said to have `ended`, which is forgotten once its stream has
 * gone. Everyone who left the room (`present`) is forgotten.
 */
export function presentingSync(
  set: PresentingSet,
  present: ReadonlySet<string>,
  sharingNow: Iterable<string>,
): PresentingSet {
  let next: Map<string, 'announced' | 'seen' | 'ended'> | null = null
  const edit = () => (next ??= new Map(set))
  const receiving = new Set(sharingNow)
  for (const id of receiving) if (!set.has(id)) edit().set(id, 'seen')
  for (const [id, state] of set) {
    if (!present.has(id) || (state === 'ended' && !receiving.has(id))) edit().delete(id)
  }
  return next ?? set
}

/** This client's place in the room, as the notices about it compare it. */
export type SelfStanding = {
  /** Who this client is in the room: its peer id, or null once it has none. */
  selfId: string | null
  role: 'host' | 'cohost' | 'attendee'
  /** Holds presenter — only an attendee's is ever in question. */
  canPresent: boolean
}

export type SelfNotice =
  | 'you-are-host'
  | 'you-are-cohost'
  | 'no-longer-host'
  | 'no-longer-cohost'
  | 'can-present'
  | 'cannot-present'

/**
 * What to tell this client about its own role, if anything.
 *
 * Only a change the room made to the same person. Leaving clears the room
 * credentials while the meeting screen is still up for the exit transition, and
 * the id roles are read by goes with them — a guest's to nothing, anyone else's
 * to their account id — so every role then reads as attendee. Taken for a
 * change, that told a co-host "You are no longer a co-host" on the way out, and
 * the notice was still on screen over the meeting's rating page.
 */
export function selfNotice(before: SelfStanding, now: SelfStanding): SelfNotice | null {
  if (!now.selfId || now.selfId !== before.selfId) return null
  if (now.role !== before.role) {
    if (now.role === 'host') return 'you-are-host'
    if (now.role === 'cohost') return 'you-are-cohost'
    return before.role === 'host' ? 'no-longer-host' : 'no-longer-cohost'
  }
  // Presenter matters only for attendees; hosts and co-hosts always can.
  if (now.role !== 'attendee' || now.canPresent === before.canPresent) return null
  return now.canPresent ? 'can-present' : 'cannot-present'
}
